import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Mail, MapPin, Phone, Search, ShoppingBag, Truck, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate, formatPrice } from '../../lib/mockData';
import { useAuthStore } from '../../store/authStore';

const orderTypeLabel: Record<string, string> = {
  LOCAL: 'Mesa',
  PICKUP: 'Retirada',
  DELIVERY: 'Delivery',
};

const paymentMethodLabel: Record<string, string> = {
  MERCADO_PAGO: 'Mercado Pago',
  CARD_ON_DELIVERY: 'Cartao na entrega',
  CASH: 'Dinheiro',
  PIX: 'Pix',
};

const paymentStatusLabel: Record<string, string> = {
  PAID: 'Pago',
  PENDING: 'Pendente',
  FAILED: 'Falhou',
  EXPIRED: 'Expirado',
};

const AdminCustomers: React.FC = () => {
  const { user } = useAuthStore();
  const [customers, setCustomers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadCustomers = useCallback(async () => {
    if (!user?.establishmentId) return;

    try {
      setLoading(true);
      const { data } = await api.get(`/customers/establishment/${user.establishmentId}`);
      setCustomers(data);
    } catch (error) {
      console.error('Failed to load customers', error);
    } finally {
      setLoading(false);
    }
  }, [user?.establishmentId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return customers;

    return customers.filter((customer) =>
      customer.name?.toLowerCase().includes(term) ||
      customer.email?.toLowerCase().includes(term) ||
      customer.phone?.toLowerCase().includes(term),
    );
  }, [customers, search]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Clientes</h1>
          <p className="mt-1 text-sm text-muted-foreground">Clientes que ja fizeram pedido neste restaurante.</p>
        </div>
      </div>

      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar cliente..."
            className="input pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-muted-foreground">Carregando clientes...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {filteredCustomers.map((customer) => (
            <div key={customer.id} className="card space-y-4 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-white">{customer.name}</h2>
                  <p className="text-sm text-muted-foreground">Primeiro pedido em {formatDate(customer.createdAt)}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Ultimo pedido em {formatDate(customer.lastOrderAt)}</p>
                </div>
                <span className="badge badge-primary">Cliente</span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span>{customer.email || 'Sem e-mail informado'}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{customer.phone || 'Sem telefone informado'}</span>
                </div>
                {customer.lastDeliveryAddress && (
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span className="leading-relaxed">{customer.lastDeliveryAddress}</span>
                  </div>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ShoppingBag className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide">Pedidos</span>
                  </div>
                  <p className="mt-2 text-xl font-black text-white">{customer.totalOrders}</p>
                </div>
                <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Total gasto</p>
                  <p className="mt-2 text-xl font-black text-primary">{formatPrice(customer.totalSpent)}</p>
                </div>
                <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Truck className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide">Ultimo pedido</span>
                  </div>
                  <p className="mt-2 text-sm font-bold text-white">
                    {customer.lastOrderType ? orderTypeLabel[customer.lastOrderType] || customer.lastOrderType : 'Nao informado'}
                  </p>
                  {customer.lastOrderStatus && (
                    <p className="mt-1 text-xs text-muted-foreground">{customer.lastOrderStatus}</p>
                  )}
                </div>
                <div className="rounded-xl border border-[#2A2A2A] bg-[#151515] p-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CreditCard className="h-4 w-4" />
                    <span className="text-xs uppercase tracking-wide">Pagamento</span>
                  </div>
                  <p className="mt-2 text-sm font-bold text-white">
                    {customer.lastPaymentMethod ? paymentMethodLabel[customer.lastPaymentMethod] || customer.lastPaymentMethod : 'Nao informado'}
                  </p>
                  {customer.lastPaymentStatus && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {paymentStatusLabel[customer.lastPaymentStatus] || customer.lastPaymentStatus}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredCustomers.length === 0 && (
            <div className="card col-span-1 p-8 text-center text-muted-foreground lg:col-span-2">
              <Users className="mx-auto mb-3 h-10 w-10 opacity-40" />
              Nenhum cliente com pedido encontrado neste restaurante.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
